import React, { useMemo, useEffect, useState } from 'react';
import { Content } from '@carbon/react';
import { useNavigate, useParams } from 'react-router-dom';
import { selectFilteredServices, services } from '../../AppSlice';
import { useAppSelector } from '../../app/hooks';
import AppHeader from '../appHeader';
import OneGUI from '../services/oneGUI';
import Watchfolder from '../services/watchfolder';
import FileQueuer from '../services/fileQueuer';
import Ingest from '../services/ingest';
import Recorder from '../services/recorder';
import Home from '../services/home';
import CompliancePlayer from '../services/compliancePlayer';
import ComplianceMonitoring from '../services/complianceMonitoring';
import ComplianceRecord from '../services/complianceRecord';
import { AppServiceId } from '../../config/build';
import { getFirstProductForTool, getProduct, products } from '../../config/products';

type ServiceKey = AppServiceId;

const isServiceKey = (value?: string): value is ServiceKey =>
  !!value && Object.prototype.hasOwnProperty.call(services, value);

const AppContent: React.FC = () => {
  const filteredServices = useAppSelector(selectFilteredServices) as ServiceKey[];
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId?: string }>();
  const [selectedProductId, setSelectedProductId] = useState<string | null>(() => {
    const savedProduct = window.sessionStorage.getItem('media-suite-product');
    return getProduct(savedProduct)?.id ?? null;
  });

  const normalizedService: ServiceKey = useMemo(() => {
    if (isServiceKey(serviceId)) {
      return serviceId;
    }
    return 'home';
  }, [serviceId]);

  useEffect(() => {
    if (!serviceId) return;
    if (!isServiceKey(serviceId)) {
      navigate('/', { replace: true });
      return;
    }
    if (serviceId !== 'home' && !filteredServices.includes(serviceId)) {
      navigate('/', { replace: true });
    }
  }, [serviceId, filteredServices, navigate]);

  const safeService: ServiceKey = useMemo(() => {
    if (normalizedService === 'home') {
      return 'home';
    }
    return filteredServices.includes(normalizedService) ? normalizedService : 'home';
  }, [normalizedService, filteredServices]);

  useEffect(() => {
    if (safeService === 'home') return;
    const selectedProduct = getProduct(selectedProductId);
    if (!selectedProduct?.tools.includes(safeService)) {
      const matchingProduct = getFirstProductForTool(safeService);
      if (matchingProduct) {
        setSelectedProductId(matchingProduct.id);
        window.sessionStorage.setItem('media-suite-product', matchingProduct.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeService]);

  const selectProduct = (productId: string) => {
    const nextProduct = products.find(product => product.id === productId && !product.externalUrl);
    if (!nextProduct) return;
    setSelectedProductId(nextProduct.id);
    window.sessionStorage.setItem('media-suite-product', nextProduct.id);
  };

  const clearProduct = () => {
    setSelectedProductId(null);
    window.sessionStorage.removeItem('media-suite-product');
  };

  const renderService = () => {
    switch (safeService) {
      case 'oneGUI':
        return <OneGUI />;
      case 'watchfolder':
        return <Watchfolder />;
      case 'fileQueuer':
        return <FileQueuer />;
      case 'ingest':
        return <Ingest />;
      case 'recorder':
        return <Recorder />;
      case 'complianceMonitoring':
        return <ComplianceMonitoring />;
      case 'compliancePlayer':
        return <CompliancePlayer />;
      case 'complianceRecord':
        return <ComplianceRecord />;
      case 'home':
      default:
        return (
          <Home
            selectedProductId={selectedProductId}
            onSelectProduct={selectProduct}
            onClearProduct={clearProduct}
          />
        );
    }
  };

  return (
    <div className={`noselect app-shell ${safeService === 'home' ? 'app-shell--home' : ''}`} style={{ width: '100%' }}>
      <AppHeader
        activeService={safeService}
        hideSideNav={safeService === 'home'}
        selectedProductId={selectedProductId}
        onGoHome={clearProduct}
      />
      <Content>{renderService()}</Content>
    </div>
  );
};

export default AppContent;
